import { ChangeDetectionStrategy, Component, OnDestroy, OnInit } from '@angular/core';
import {
    FormControl,
    FormGroup,
    FormsModule,
    ReactiveFormsModule,
    Validators,
} from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { Subject, takeUntil } from 'rxjs';
import { NavigationText } from '../../../../core/constants/navigation-text';
import { UserRoutes } from '../../../../core/constants/routes';
import {
    UserRole,
    UserRoleLabelMapping,
} from '../../../../core/enums/users/role';
import { BlockNavigationIfChange } from '../../../../core/models/block-navigation-if-change';
import { OrganizationSearchResponse } from '../../../../core/models/civi-crm/organization-search-response';
import { FormGroupType } from '../../../../core/models/form-group-type';
import { RegExPattern } from '../../../../core/models/regexp-pattern/regex-pattern';
import { UserStatus } from '../../../../core/models/user-status';
import { AlertService } from '../../../../core/services/alert/alert.service';
import {
    OrganizationType,
    OtherOrganizationTypeValue,
} from '../../../../core/services/resources/models/organization-type';
import { ResourcesService } from '../../../../core/services/resources/resources.service';
import { ModalComponent } from '../../../../shared/components/modal/modal.component';
import { NavigationOptions } from '../../../../shared/components/page-header-nav/models/navigation-options';
import { PageHeaderNavItem } from '../../../../shared/components/page-header-nav/models/page-header-nav-item';
import { PageHeaderNavComponent } from '../../../../shared/components/page-header-nav/page-header-nav.component';
import { GridUser } from '../../shared/models/grid-user';
import { UserInformationService } from '../../shared/services/user-information/user-information.service';
import { UserManagementService } from '../../shared/services/user-management/user-management.service';
import { OrganizationSearchComponent } from '../components/organization-search/organization-search.component';
import {
    UserApproveModalText,
    UserRejectModalText,
} from './constants/modal-text';
import { UserReviewMessage } from './constants/user-review-message';
import { ReviewUser } from './models/review-user';
import { UserReviewRequest } from './models/user-review-request';

@Component({
    selector: 'app-user-review',
    imports: [
        MatSelectModule,
        MatInputModule,
        MatFormFieldModule,
        MatTooltipModule,
        MatIconModule,
        FormsModule,
        ReactiveFormsModule,
        MatAutocompleteModule,
        RouterModule,
        PageHeaderNavComponent,
        MatDialogModule,
        OrganizationSearchComponent,
    ],
    changeDetection: ChangeDetectionStrategy.Eager,
    templateUrl: './user-review.component.html',
})
export class UserReviewComponent
    implements OnInit, OnDestroy, BlockNavigationIfChange
{
    hasChanges: boolean = false;
    user: ReviewUser = {} as ReviewUser;
    organizationMatch = false;
    filteredOrganizations: OrganizationSearchResponse[] = [];
    returnRoute = UserRoutes.getFullRoute('INDEX');
    originalOrganizationName: string = '';
    organizationTypes: OrganizationType[] = [];

    form: FormGroup<FormGroupType<UserReviewRequest>> = new FormGroup<
        FormGroupType<UserReviewRequest>
    >({
        organizationName: new FormControl(undefined, Validators.required),
        role: new FormControl(undefined, Validators.required),
        isApproved: new FormControl(undefined),
        organizationType: new FormControl(undefined, Validators.required),
        otherOrganizationType: new FormControl(undefined, Validators.required),
    });

    pageHeaderNavItems: NavigationOptions = {};
    regExPattern = RegExPattern;

    private destroy$ = new Subject<void>();

    private readonly modalData = {
        reject: {
            title: UserRejectModalText.FULL_TITLE,
            content: UserRejectModalText.CONTENT,
            primaryAction: {
                title: UserRejectModalText.TITLE,
                classList: '!bg-error-700 !text-tertiary-50',
                action: this.reject.bind(this),
            },
        },
        approve: {
            title: UserApproveModalText.FULL_TITLE,
            primaryAction: {
                title: UserApproveModalText.TITLE,
                classList: '!bg-primary-700 !text-tertiary-50',
                action: this.approve.bind(this),
            },
        },
    };

    readonly userRoles: UserRole[] = Object.values(UserRole).filter(
        (role) => typeof role === 'number',
    ) as UserRole[];

    readonly UserRoleLabelMapping = UserRoleLabelMapping;

    private id: string;
    public userResponse: GridUser | undefined;

    saveButton: PageHeaderNavItem = {
        title: NavigationText.SAVE,
        classList:
            '!rounded-lg !bg-tertiary-700 !text-tertiary-50 disabled:!bg-gray-300 disabled:!text-gray-500',
        action: this.saveChanges.bind(this),
        disabled: false,
    };

    constructor(
        private readonly route: ActivatedRoute,
        private readonly userManagementService: UserManagementService,
        private readonly userInformationService: UserInformationService,
        private readonly resourcesService: ResourcesService,
        private readonly alertService: AlertService,
        private readonly router: Router,
        private readonly dialog: MatDialog,
    ) {
        this.id = this.route.snapshot.paramMap.get('id')!;
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    async ngOnInit() {
        this.getOrganizationTypes();
        this.userManagementService
            .get(+this.id)
            .then(async (user: GridUser) => {
                this.userResponse = user;
                this.user = await this.transformUserData(user);
                this.form.setValue({
                    organizationName: this.user.organizationName ?? '',
                    organizationType:
                        user?.userRegistration?.organizationType ?? '',
                    otherOrganizationType:
                        user?.userRegistration?.otherOrganizationType ?? '',
                    role: this.user.role,
                    isApproved: null,
                });

                this.originalOrganizationName =
                    this.user.organizationName ?? '';

                this.form.valueChanges
                    .pipe(takeUntil(this.destroy$))
                    .subscribe(() => {
                        this.hasChanges = this.form.dirty;
                    });

                this.subscribeToOrganizationTypeChanges();
                this.setPageHeaderNavItems();
            });
    }

    onOrganizationMatchChange(match: boolean): void {
        this.organizationMatch = match;
    }

    showOrganizationTypeOtherField(): boolean {
        return (
            this.form.controls.organizationType?.value ===
            OtherOrganizationTypeValue
        );
    }

    private async transformUserData(user: GridUser): Promise<ReviewUser> {
        return {
            username: user?.user?.username,
            fullName: this.userInformationService.getFullName(user),
            email: user?.user?.email,
            organizationName: user?.userRegistration?.organizationName,
            streetAddress: user?.userRegistration?.streetAddress,
            city: user?.userRegistration?.city,
            stateProvince: user?.userRegistration?.stateProvinceId
                ? await this.resourcesService.getStateProvince(
                      user?.userRegistration?.stateProvinceId,
                  )
                : undefined,
            postalCode: user?.userRegistration?.postalCode,
            phoneNumber: this.userInformationService.getPhoneNumber(
                user?.userRegistration,
            ),
            role: user?.user.role,
            status: UserStatus.getUserStatus(user.user),
        };
    }

    private openRejectModal(): void {
        this.dialog.open(ModalComponent, {
            disableClose: true,
            data: this.modalData.reject,
        });
    }

    private openApproveModal(): void {
        const organizationName = this.form.controls.organizationName!.value;
        let organizationText = '';

        if (this.organizationMatch) {
            organizationText = UserApproveModalText.ORGANIZATION_ASSIGN_CONTENT;
        } else {
            organizationText = UserApproveModalText.ORGANIZATION_CREATE_CONTENT;
        }

        if (organizationName) {
            organizationText = organizationText.replace(
                UserApproveModalText.ORGANIZATION_NAME_TOKEN,
                organizationName,
            );
        }

        this.dialog.open(ModalComponent, {
            disableClose: true,
            data: {
                ...this.modalData.approve,
                content: organizationText,
            },
        });
    }

    private approve(): void {
        this.form.patchValue({
            isApproved: true,
        });

        this.submit({ alert: { label: UserReviewMessage.USER_APPROVED } });
    }

    private reject(): void {
        this.form.patchValue({
            isApproved: false,
        });

        this.submit({ alert: { label: UserReviewMessage.USER_REJECTED } });
    }

    private submit(options?: {
        redirect?: boolean;
        alert?: { label: string };
    }) {
        this.hasChanges = false;

        const userReviewRequest: UserReviewRequest = {
            ...this.form.value,
        };

        this.userManagementService
            .review(+this.id, userReviewRequest)
            .then(() => {
                if (options?.alert) {
                    this.alertService.displaySuccessAlert(options.alert.label);
                }

                if (options?.redirect === false) {
                    return;
                }

                this.router.navigate([this.returnRoute]);
            })
            .catch((error) => {
                console.error(error);
            });
    }

    private saveChanges(): void {
        this.form.setValue({
            ...this.form.value,
            isApproved: null,
        });

        this.submit({
            redirect: false,
            alert: { label: UserReviewMessage.USER_CHANGES_SAVED },
        });
    }

    /**
     * Sets the navigation items for the page header based on the user's approval status.
     */
    private setPageHeaderNavItems(): void {
        this.pageHeaderNavItems = {
            quaternaries: [
                {
                    title: NavigationText.RETURN_TO_GRID,
                    routerLink: this.returnRoute,
                    classList: '!rounded-lg !border-primary-700',
                },
            ],
            tertiaries: [this.saveButton],
            // If the user has been already rejected, then hide the reject button
            secondaries:
                this.userResponse?.user.isApproved != false
                    ? [
                          {
                              title: UserRejectModalText.TITLE,
                              classList:
                                  '!rounded-lg !bg-error-700 !text-tertiary-50',
                              action: this.openRejectModal.bind(this),
                          },
                      ]
                    : undefined,
            primaries: [
                {
                    title: UserApproveModalText.TITLE,
                    classList: '!rounded-lg',
                    action: this.openApproveModal.bind(this),
                },
            ],
        };
    }

    private getOrganizationTypes(): void {
        this.resourcesService
            .getOrganizationTypes()
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (organizationTypes: OrganizationType[]) => {
                    this.organizationTypes = organizationTypes;
                },
                error: (e) => {
                    console.error(e);
                    this.form.controls.organizationType?.disable();
                },
            });
    }

    private subscribeToOrganizationTypeChanges(): void {
        this.form.controls.organizationType?.valueChanges
            .pipe(takeUntil(this.destroy$))
            .subscribe((value) => {
                if (value !== OtherOrganizationTypeValue) {
                    this.form.controls.otherOrganizationType?.setValue('');
                } else {
                    this.saveButton.disabled = this.form.invalid;
                }
            });
        this.form.controls.otherOrganizationType?.valueChanges
            .pipe(takeUntil(this.destroy$))
            .subscribe(() => {
                this.form.controls.otherOrganizationType?.updateValueAndValidity(
                    { emitEvent: false },
                );
                this.saveButton.disabled =
                    this.form.controls.organizationType?.value ===
                        OtherOrganizationTypeValue && this.form.invalid;
            });
    }
}
